import { AsyncPipe, CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute } from '@angular/router';
import { SetCurrentApp } from '@app/core/states/app.actions';
import { LogEvent, SetEventProperties } from '@app/core/states/event-tracking.actions';
import { SkipToStep } from '@app/core/states/stepper.actions';
import { StepperComponent } from '@app/core/stepper/stepper.component';
import { AppConfigApi } from '@app/features/app-content/models/app-config.model';
import { FetchAppConfigRequest } from '@app/features/app-content/states/app-configuration.actions';
import { AppConfigurationState } from '@app/features/app-content/states/app-configuration.state';
import { UiModule } from '@app/ui';
import { UIState } from '@app/ui/states/ui.state';
import { TranslateModule } from '@ngx-translate/core';
import { Select, Store } from '@ngxs/store';
import { Observable, Subscription } from 'rxjs';
import { AppConfigurationModule } from '../app-content/app-configuration.module';
import { FinishCreationFlowService } from '../app-settings/services/finish-creation-flow.service';
import { AdmobComponent } from './admob/admob.component';

@Component({
  selector: 'rk-monetization',
  templateUrl: './monetization.component.html',
  styleUrls: ['./monetization.component.scss'],
  imports: [
    AppConfigurationModule,
    UiModule,
    AdmobComponent,
    StepperComponent,
    TranslateModule,
    AsyncPipe,
    CommonModule,
  ],
})
export class MonetizationComponent implements OnInit, OnDestroy {
  @Select(AppConfigurationState.currentAppConfig)
  currentAppConfig$: Observable<AppConfigApi>;

  @Select(UIState.isCreationProcess)
  isCreationProcess$: Observable<boolean>;

  @Select(AppConfigurationState.monetizationAvailable)
  monetizationAvailable$: Observable<boolean>;

  @Select(AppConfigurationState.isFinishingPublication)
  isFinishingPublication$: Observable<boolean>;

  @Select(AppConfigurationState.currentAppIdPlan)
  currentAppIdPlan$: Observable<number>;

  @Select(AppConfigurationState.isPaidApp)
  isPaidApp$: Observable<boolean>;

  nextStepIsDisabled = false;

  private readonly subscriptions: Subscription = new Subscription();

  private planId: number;

  constructor(
    private readonly store: Store,
    private readonly route: ActivatedRoute,
    private readonly dialog: MatDialog,
    private readonly finishCreationFlow: FinishCreationFlowService,
  ) {}

  ngOnInit() {
    // adjust stepper position
    this.subscriptions.add(
      this.isPaidApp$.subscribe(isPaidApp => {
        if (isPaidApp) {
          this.store.dispatch(new SkipToStep(3));
        } else {
          this.store.dispatch(new SkipToStep(4));
        }
      }),
    );

    // fetch config
    this.subscriptions.add(
      this.route.params.subscribe(params => {
        const appId = params['appId'];
        if (appId) {
          this.store.dispatch(new FetchAppConfigRequest(appId));
          this.store.dispatch(new SetCurrentApp(Number(appId)));
        }
      }),
    );
    this.subscriptions.add(
      this.currentAppIdPlan$.subscribe(id => {
        this.planId = id;
      }),
    );
    // event tracking
    this.store.dispatch(
      new SetEventProperties({
        name: 'PageCreationMonetization',
        component: 'Page',
      }),
    );
    this.store.dispatch(new LogEvent('Page Viewed'));
  }

  finishCreation = (): void => {
    this.finishCreationFlow.run();
  };

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
